import {
  DisposalSite,
  DisposalSiteModel,
} from '@digital-platform/disposal-sites/domain';
import { DocusignData } from '@digital-platform/docusign/domain';
import { Generator, GeneratorModel } from '@digital-platform/generators/domain';
import { DomainModelHelper, EntityBase } from '@digital-platform/shared/domain';
import {
  SignNowDocument,
  SignNowDocumentModel,
} from '@digital-platform/sign-now/domain';
import { User } from '@digital-platform/users/domain';
import { InternalStatus } from '../enums/internal-status';
import { WasteProfileStatus } from '../enums/waste-profile-status';
import { WasteProfileCustomerCard } from '../interfaces/waste-profile-customer-card.interface';
import {
  ChangeRequestDetails,
  ChangeRequestDetailsModel,
  RecertificationDetails,
  RecertificationDetailsModel,
} from '../waste-profiles-domain';

export class WasteProfileCustomerCardModel
  extends EntityBase
  implements WasteProfileCustomerCard
{
  static readonly DEFAULT_CONFIG: WasteProfileCustomerCard = {
    schemaVersion: 1,
    type: null,
    lastModifiedDate: null,
    expiryDate: null,
    id: null,
    name: '',
    number: '',
    status: WasteProfileStatus.NOT_SUBMITTED,
    internalStatus: InternalStatus.PENDING_CUSTOMER_INFO,
    generator: null,
    destination: null,
    isRecertification: false,
    isChange: false,
    pendingRecertification: null,
    pendingChangeRequest: null,
    analyticalRequiredOnNextRecert: false,
    signNowDocument: null,
    docusignData: null,
    stateApprovalWasteCode: '',
    customerName: null,
    approvedDate: null,
  };
  // meta info
  readonly schemaVersion: 1;
  readonly type: string;
  readonly lastModifiedDate: Date;
  readonly status: WasteProfileStatus;
  readonly internalStatus: InternalStatus;
  readonly expiryDate: Date;
  readonly generator: Generator;
  readonly name: string;
  readonly destination: DisposalSite;
  readonly number: string;
  readonly coordinator: User;
  readonly approverName: string;
  readonly id: string;
  readonly approvedDate: Date;
  readonly isRecertification: boolean;
  readonly isChange: boolean;
  readonly submittedDate: Date;
  readonly pendingRecertification: RecertificationDetails;
  readonly pendingChangeRequest: ChangeRequestDetails;
  readonly analyticalRequiredOnNextRecert: boolean;
  readonly signNowDocument: SignNowDocument;
  readonly docusignData: DocusignData;
  readonly stateApprovalWasteCode: string;
  readonly customerName: string;

  constructor(config?: WasteProfileCustomerCard) {
    super(config);
    this.schemaVersion = config ? config.schemaVersion : 1;
    this.isRecertification = config ? config.isRecertification : false;
    this.isChange = config ? config.isChange : false;

    this.expiryDate = config
      ? config.expiryDate
        ? DomainModelHelper.ensureDataIsDate(config.expiryDate)
        : null
      : null;
    this.approvedDate = config
      ? DomainModelHelper.ensureDataIsDate(config.approvedDate)
      : null;
    this.destination = config
      ? this.createDisposalSite(config.destination)
      : null;
    this.name = config
      ? DomainModelHelper.ensureDataIsString(config.name)
      : null;
    this.number = config
      ? DomainModelHelper.ensureDataIsString(config.number)
      : null;
    this.status = config.status
      ? config.status
      : WasteProfileStatus.NOT_SUBMITTED;
    this.internalStatus = config.internalStatus
      ? config.internalStatus
      : InternalStatus.PENDING_CUSTOMER_INFO;
    this.generator = config
      ? this.createGenerator(config.generator)
      : this.createGenerator(GeneratorModel.DEFAULT_CONFIG);
    this.pendingRecertification = config
      ? this.createPendingRecertification(config.pendingRecertification)
      : null;
    this.pendingChangeRequest = config
      ? this.createPendingChangeRequest(config.pendingChangeRequest)
      : null;
    this.analyticalRequiredOnNextRecert = config
      ? config.analyticalRequiredOnNextRecert || false
      : false;
    this.signNowDocument = config
      ? this.createSignNowDocument(config.signNowDocument)
      : this.createSignNowDocument(SignNowDocumentModel.DEFAULT_CONFIG);
    this.docusignData = config ? config.docusignData : null;
    this.stateApprovalWasteCode = config
      ? DomainModelHelper.ensureDataIsString(config.stateApprovalWasteCode)
      : null;
    this.type = config ? config.type : null;
    this.customerName = config
      ? DomainModelHelper.ensureDataIsString(config.customerName)
      : null;
  }

  protected createGenerator(generatorConfig: Generator): Generator {
    if (generatorConfig) {
      return new GeneratorModel(generatorConfig);
    }
    return null;
  }

  protected createDisposalSite(disposalSiteConfig: DisposalSite) {
    if (disposalSiteConfig) {
      return new DisposalSiteModel(disposalSiteConfig);
    }
    return null;
  }

  protected createSignNowDocument(signNowDocumentConfig: SignNowDocument) {
    if (signNowDocumentConfig) {
      return new SignNowDocumentModel(signNowDocumentConfig);
    }
    return null;
  }

  protected createPendingRecertification(
    recertificationConfig: RecertificationDetails
  ): RecertificationDetails {
    if (recertificationConfig) {
      return new RecertificationDetailsModel(recertificationConfig);
    }

    return null;
  }

  protected createPendingChangeRequest(
    changeRequestConfig: ChangeRequestDetails
  ): ChangeRequestDetails {
    if (changeRequestConfig) {
      return new ChangeRequestDetailsModel(changeRequestConfig);
    }

    return null;
  }
}
